import React, { useMemo } from 'react'
import { useDefaultValueTriggerUpdate, useRemoveMandatoryError } from "../Utils/FormBuilderUtils";
import { Controller } from "react-hook-form";
import { formRulesBuilder } from "../lib/FormBuilderRules";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import { FormBuilder, formBuilderParams } from "../FormBuilder";
import { HexColorPicker } from "react-colorful";
import { Box, Button, InputAdornment, Popover } from "@mui/material";
import { Colorize } from "@mui/icons-material";
import useColors from "utils/useColors/useColors";

/**
 * @typedef {import("../lib/FormBuilderFieldBuilder").baseFormComponentProps} baseFormComponentProps
 * @typedef {import("../lib/FormBuilderFieldBuilder").baseConfig} baseConfig
 * @typedef {import("../lib/FormBuilderFieldBuilder").colorPickerConfig} colorPickerConfig
 * @param {baseFormComponentProps & baseConfig & colorPickerConfig} props
 */
const FormBuilderColorPicker = ({
  formProps,
  fieldName,
  defaultValue = "",
  defaultValueTriggerUpdate = false,
  mandatory = false,
  customRule,
  colorType = "hex",
  label,
  hideLabel,
  noErrorMessage = false,
  placeholder,
  customOnChangeFn,
  disabled = false,
  readOnly = false,
}) => {
  const c = useColors();

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [colorPickerVisible, setColorPickerVisible] = React.useState(false);

  useRemoveMandatoryError({ fieldName, mandatory, formProps });
  defaultValueTriggerUpdate && useDefaultValueTriggerUpdate({ fieldName, defaultValue, formProps });

  const colorRules = useMemo(() => {
    switch (colorType) {
      case "hex":
        return ["hexColor"];
      default:
        return [];
    }
  }, [mandatory, customRule]);

  return (
    <Controller
      key={fieldName}
      name={fieldName}
      control={formProps.control}
      defaultValue={defaultValue}
      rules={formRulesBuilder({ mandatory, rules: colorRules, customRule })}
      render={({ field: { value, onChange, ref }, fieldState: { error } }) => {
        return (
          <>
            <OutlinedText
              inputRef={ref}
              label={label}
              hideLabel={hideLabel === "removePadding"}
              emptyLabel={hideLabel === "keepPadding"}
              noErrorMessage={noErrorMessage}
              placeholder={placeholder}
              value={value ?? ""}
              onchangeFn={(e) => {
                const newValue = e.target.value;
                onChange(newValue);
                setColorPickerVisible(false);
                customOnChangeFn && customOnChangeFn(newValue, formProps);
              }}
              errorMessage={error?.message || ""}
              sxParams={{
                textError: !!error,
              }}
              divStyle={formBuilderParams.formItemStyle}
              disabled={disabled}
              readOnly={readOnly}
              mandatory={mandatory}
              endAdornment={
                <InputAdornment position="end">
                  <Button
                    variant="adornment-button"
                    disabled={disabled || readOnly}
                    onClick={(event) => {
                      setColorPickerVisible(!colorPickerVisible);
                      setAnchorEl(event.currentTarget);
                    }}
                  >
                    {
                      colorRules.every((rule) => FormBuilder.rules[rule].rule(value)) ?
                        <Box
                          sx={{
                            minHeight: "16px",
                            height: "16px",
                            minWidth: "16px",
                            width: "16px",
                            borderRadius: "50%",
                            backgroundColor: value,
                            border: `2px solid ${c.blackLightCCC}`,
                          }}
                        /> : <Colorize sx={{ fontSize: "18px", color: "#666" }} />
                    }
                  </Button>
                </InputAdornment>
              }
            />
            <Popover
              open={colorPickerVisible}
              anchorEl={disabled || readOnly ? null : anchorEl}
              onClose={() => setColorPickerVisible(false)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              slotProps={{
                paper: {
                  sx: {
                    background: "none",
                    boxShadow: "none",
                    overflow: "hidden",
                  },
                },
              }}
            >
              <HexColorPicker
                // special handling for preventing #NaNNaNNaN when sliding the hue with an empty string
                color={value || "#000000"}
                onChange={(color) => {
                  onChange(color);
                  customOnChangeFn && customOnChangeFn(color, formProps);
                }} />
            </Popover>
          </>
        )
      }}

    />
  )
}


export default FormBuilderColorPicker